import { gsap } from 'gsap';

export function initSearch() {
  const searchToggleBtn = document.querySelector('.search-toggle-btn');
  const searchOverlay = document.getElementById('searchOverlay');
  const searchCloseBtn = document.querySelector('.search-overlay__close');
  const searchBackdrop = document.querySelector('.search-overlay__backdrop');
  const searchInput = document.getElementById(
    'searchInput'
  ) as HTMLInputElement;
  const searchResults = document.getElementById('searchResults');

  if (
    !searchToggleBtn ||
    !searchOverlay ||
    !searchCloseBtn ||
    !searchInput ||
    !searchResults
  ) {
    return;
  }

  // Track search state
  let hasResults = false;
  let loadingTimeout: number;

  function openSearch() {
    searchOverlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      searchInput.focus();
    }, 100);
  }

  function closeSearch() {
    searchOverlay?.classList.remove('active');
    document.body.style.overflow = '';
    searchInput.value = '';
    hasResults = false; // Reset results state
    clearTimeout(loadingTimeout); // Clear any pending loading
    showPlaceholder();
  }

  function showPlaceholder() {
    if (searchResults) {
      searchResults.innerHTML = `
      <div class="search-results__placeholder">
        <span class="icon icon-search1"></span>
        <p>Start typing to search...</p>
      </div>
    `;
    }
  }

  function showLoading(immediate = false) {
    if (immediate || !hasResults) {
      // Show loading immediately for first search or when forced
      if (searchResults) {
        searchResults.innerHTML = `
        <div class="search-results__loading">
          <div class="spinner"></div>
          <span>Searching...</span>
        </div>
      `;
      }
    }
    // For subsequent searches, only show loading if request takes too long
  }

  function showLoadingDelayed() {
    loadingTimeout = window.setTimeout(() => {
      if (hasResults && searchResults) {
        // Fade to loading state if we still don't have new results
        gsap.to(searchResults.children, {
          opacity: 0.5,
          duration: 0.2,
          ease: 'power2.out',
        });
      }
    }, 300); // Show loading after 300ms delay
  }

  function showResults(results: any[]) {
    // Clear any pending loading timeout
    clearTimeout(loadingTimeout);

    // Update results state
    hasResults = results.length > 0;

    // Capture current height before content changes
    const startHeight = searchResults?.offsetHeight;

    // First, fade out current content
    if (searchResults) {
      gsap.to(searchResults.children, {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          if (results.length === 0 && searchResults) {
            searchResults.innerHTML = `
            <div class="search-results__empty">
              <span class="icon icon-search1"></span>
              <p>No results found</p>
            </div>
          `;

            // Animate empty state in
            gsap.fromTo(
              searchResults.children,
              { opacity: 0, y: 10 },
              { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
            );
            return;
          }

          const resultsHTML = results
            .map(
              (result) => `
        <div class="search-result__item" onclick="window.location.href='${encodeURI(
          result.url
        )}'">
          ${
            result.image
              ? `<div class="search-result__image">
            <img src="${result.image}" alt="${result.title}" loading="lazy" />
          </div>`
              : ''
          }
          <div class="search-result__content">
            <span class="search-result__category">${result.collection}</span>
            <h3 class="search-result__title">${result.title}</h3>
            <p class="search-result__excerpt">${result.excerpt}</p>
            <div class="search-result__meta">
              <span class="search-result__date">${result.date}</span>
            </div>
          </div>
        </div>
      `
            )
            .join('');

          // Set content with hidden items
          searchResults.innerHTML = `
          <div class="search-results__list">
            ${resultsHTML}
          </div>
        `;

          // Force layout calculation to get accurate target height
          searchResults.style.height = 'auto';
          const resultsList = searchResults.querySelector(
            '.search-results__list'
          ) as HTMLElement;
          const targetHeight = resultsList.scrollHeight;

          // Animate from start height to target height
          gsap.fromTo(
            searchResults,
            { height: startHeight },
            {
              height: targetHeight,
              duration: 0.5,
              ease: 'power2.Out',
              onComplete: () => {
                gsap.set(searchResults, { height: 'auto' });
              },
            }
          );

          // Animate results in with staggered timing
          const resultItems = searchResults.querySelectorAll(
            '.search-result__item'
          );
          gsap.fromTo(
            resultItems,
            {
              opacity: 0,
              y: 20,
            },
            {
              opacity: 1,
              y: 0,
              duration: 1,
              ease: 'power2.out',
              stagger: 0.1,
              delay: 0.2, // Start after height animation begins
            }
          );
        },
      });
    }
  }

  async function performSearch(query: string) {
    if (!query.trim()) {
      showPlaceholder();
      return;
    }

    // Smart loading logic
    if (!hasResults) {
      // Show loading immediately for first search
      showLoading(true);
    } else {
      // For subsequent searches, only show loading if request is slow
      showLoadingDelayed();
    }

    const searchCollections = ['news', 'stories', 'insightsAndData', 'events'];
    const searches = searchCollections.map((collection) => ({
      collection: collection,
      q: query,
      query_by: 'title,excerpt,content',
      sort_by: '_text_match:desc',
      per_page: 10,
      highlight_full_fields: 'title,excerpt',
      snippet_threshold: 30,
    }));

    try {
      const multiSearchQuery = {
        searches,
        union: true,
      };

      const response = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(multiSearchQuery),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Flatten all results from multiple collections
      // const allHits = data.results.flatMap((result: any) =>
      //   result.hits ? result.hits : []
      // );

      const allHits = data.hits ?? [];

      if (allHits.length > 0) {
        const formattedResults = allHits.map((hit: any) => {
          const doc = hit.document;
          return {
            title:
              hit.highlights &&
              hit.highlights[0] &&
              hit.highlights[0].field === 'title'
                ? hit.highlights[0].snippet || doc.title
                : doc.title,
            excerpt:
              hit.highlights &&
              hit.highlights.find((h: any) => h.field === 'excerpt')
                ? hit.highlights.find((h: any) => h.field === 'excerpt').snippet
                : doc.excerpt ||
                  doc.content?.substring(0, 200) + '...' ||
                  'No excerpt available',
            collection: doc.handle ?? doc.calendar_name ?? doc.collection,
            date: doc.post_date_timestamp
              ? new Date(doc.post_date_timestamp * 1000).toLocaleDateString(
                  'en-US',
                  {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }
                )
              : doc.start_date_timestamp
              ? new Date(doc.start_date_timestamp * 1000).toLocaleDateString(
                  'en-US',
                  { year: 'numeric', month: 'long', day: 'numeric' }
                )
              : 'No date',
            dateTimestamp: doc.post_date_timestamp || 0,
            url: doc.url,
            image: doc.cover_image || null,
          };
        });

        // Sort by date (newest first)
        // formattedResults.sort(
        //   (a: { dateTimestamp: number }, b: { dateTimestamp: number }) =>
        //     b.dateTimestamp - a.dateTimestamp
        // );

        showResults(formattedResults);
      } else {
        showResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);

      let errorMessage =
        'Search temporarily unavailable. Please try again later.';

      if (error instanceof Error) {
        if (error.message.includes('NetworkError')) {
          errorMessage =
            'Unable to connect to search service. Please check your connection.';
        }
      }

      if (searchResults) {
        searchResults.innerHTML = `
      <div class="search-results__empty">
        <span class="icon icon-warning"></span>
        <p>${errorMessage}</p>
        <button class="retry-search-btn" onclick="performSearch('${query.replace(
          /'/g,
          '\\'
        )}')">
          Try Again
        </button>
      </div>`;
      }
    }
  }

  // Event listeners
  searchToggleBtn.addEventListener('click', openSearch);
  searchCloseBtn.addEventListener('click', closeSearch);
  searchBackdrop?.addEventListener('click', closeSearch);

  // ESC key handler
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && searchOverlay.classList.contains('active')) {
      closeSearch();
    }
  });

  // Search input handler with debounce
  let searchTimeout: number;
  searchInput.addEventListener('input', (e) => {
    const query = (e.target as HTMLInputElement).value;

    clearTimeout(searchTimeout);
    searchTimeout = window.setTimeout(() => {
      performSearch(query);
    }, 300);
  });

  // Initialize placeholder
  showPlaceholder();
}
